/* eslint-disable no-multi-assign */
/* eslint-disable no-nested-ternary */
import { throttle } from 'lodash';
import Vue from 'vue';
import { getScrollContainer } from '@/libs/dom';

export function isHtmlElement(node: HTMLElement) {
    return node && node.nodeType === Node.ELEMENT_NODE;
}

/**
 *  - Inspired:
 *    https://github.com/jashkenas/underscore/blob/master/modules/isFunction.js
 */
let isFunction = (functionToCheck: any) => {
    const getType = {};
    return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]';
};

if (
    typeof /./ !== 'function' && typeof Int8Array !== 'object' && (Vue.prototype.$isServer || typeof document.childNodes !== 'function')
) {
    isFunction = (obj) => typeof obj === 'function' || false;
}

// eslint-disable-next-line no-void
export const isUndefined = (val: any) => val === void 0;

export const isDefined = (val: any) => val !== undefined && val !== null;

const getStyleComputedProperty = (element: any, property: any): any => {
    if (element === window) {
        element = document.documentElement;
    }

    if (element.nodeType !== 1) {
        return [];
    }
    // NOTE: 1 DOM access here
    const css = window.getComputedStyle(element, null);
    return property ? css[property] : css;
};

const entries = (obj: any) => Object.keys(obj || {}).map((key) => [key, obj[key]]);

const getPositionSize = (el: any, prop: any) => (el === window || el === document ? (document as any).documentElement[prop] : el[prop]);

const getOffsetHeight = (el: any) => getPositionSize(el, 'offsetHeight');

const getClientHeight = (el: any) => getPositionSize(el, 'clientHeight');

const scope = 'InfiniteScroll';
const attributes = {
    delay: {
        type: Number,
        default: 200,
    },
    distance: {
        type: Number,
        default: 0,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    immediate: {
        type: Boolean,
        default: true,
    },
};

const getScrollOptions = (el: any, vm: any) => {
    if (!isHtmlElement(el)) return {};

    return entries(attributes).reduce((map: any, [key, option]: any) => {
        const { type, default: defaultValue } = option;
        let value = el.getAttribute(`infinite-scroll-${key}`);
        value = isUndefined(vm[value]) ? value : vm[value];
        switch (type) {
            case Number:
                value = Number(value);
                value = Number.isNaN(value) ? defaultValue : value;
                break;
            case Boolean:
                value = isDefined(value) ? value === 'false' ? false : Boolean(value) : defaultValue;
                break;
            default:
                value = type(value);
        }
        map[key] = value;
        return map;
    }, {});
};

const getElementTop = (el: any) => el.getBoundingClientRect().top;

const handleScroll = function handleScroll(cb: any) {
    const {
        el, vm, container, observer,
    } = this[scope];
    const { distance, disabled } = getScrollOptions(el, vm);

    if (disabled) return;

    const containerInfo = container.getBoundingClientRect();
    if (!containerInfo.width && !containerInfo.height) return;

    let shouldTrigger = false;

    if (container === el) {
        // be aware of difference between clientHeight & offsetHeight & window.getComputedStyle().height
        const scrollBottom = container.scrollTop + getClientHeight(container);
        shouldTrigger = container.scrollHeight - scrollBottom <= distance;
    } else {
        const heightBelowTop = getOffsetHeight(el) + getElementTop(el) - getElementTop(container);
        const offsetHeight = getOffsetHeight(container);
        const borderBottom = Number.parseFloat(
            getStyleComputedProperty(container, 'borderBottomWidth'),
        );
        shouldTrigger = heightBelowTop - offsetHeight + borderBottom <= distance;
    }

    if (shouldTrigger && isFunction(cb)) {
        cb.call(vm);
    } else if (observer) {
        observer.disconnect();
        this[scope].observer = null;
    }
};

const InfiniteScroll = {
    name: 'InfiniteScroll',
    // eslint-disable-next-line no-shadow
    install(Vue: any) {
        Vue.directive(InfiniteScroll.name, InfiniteScroll);
    },
    inserted(el: any, binding: any, vnode: any) {
        const cb = binding.value;

        const vm = vnode.context;
        // only include vertical scroll
        // const container = getScrollContainer(el, true);
        const container = el;
        const { delay, immediate } = getScrollOptions(el, vm);
        const onScroll = throttle(handleScroll.bind(el, cb), delay);

        el[scope] = {
            el, vm, container, onScroll,
        };

        if (container) {
            container.addEventListener('scroll', onScroll);

            if (immediate) {
                const observer = (el[scope].observer = new MutationObserver(onScroll));
                observer.observe(container, { childList: true, subtree: true });
                onScroll();
            }
        }
    },
    unbind(el: any) {
        const { container, onScroll, observer } = el[scope];
        if (container) {
            container.removeEventListener('scroll', onScroll);
        }
        if (observer) {
            observer.disconnect();
        }
    },
};
export default InfiniteScroll;
